import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import { useTranslation } from 'react-i18next';

/**
 * Ürün notu modalında (sepetteki ürün > Açıklama) tek dokunuşla eklenen hazır
 * açıklamalar. Eskiden ItemNoteModal içinde sabit bir listeydi; artık ekran
 * bazında bu sekmeden yönetilir ve QuickSaleScreens.PredefinedItemNotesJson'a
 * kaydedilir.
 */

// Restoranların çoğunda işe yarayan başlangıç listesi. Zorunlu değil; sekmedeki
// "önerilenleri ekle" butonu ile tek tıkla eklenir.
export const SUGGESTED_ITEM_NOTES = [
  'Az Pişmiş',
  'Çok Pişmiş',
  'Acısız',
  'Bol Soslu',
  'Soğansız',
  'Ketçapsız',
  'Mayonezsiz',
  'Az Tuzlu',
  'Yanında Sos',
];

const MAX_NOTE_LENGTH = 60;
const MAX_NOTE_COUNT = 40;

interface ItemNotesSettingsTabProps {
  notes: string[];
  onChange: (notes: string[]) => void;
}

const ItemNotesSettingsTab: React.FC<ItemNotesSettingsTabProps> = ({ notes, onChange }) => {
  const { t } = useTranslation('salesSystem');
  const [newNote, setNewNote] = useState('');

  const trimmed = newNote.trim();
  const isDuplicate = trimmed.length > 0 && notes.includes(trimmed);
  const isFull = notes.length >= MAX_NOTE_COUNT;

  const handleAdd = () => {
    if (!trimmed || isDuplicate || isFull) return;
    onChange([...notes, trimmed]);
    setNewNote('');
  };

  const handleDelete = (index: number) => {
    onChange(notes.filter((_, i) => i !== index));
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= notes.length) return;
    const next = [...notes];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const missingSuggestions = SUGGESTED_ITEM_NOTES.filter(s => !notes.includes(s));

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <NoteAddIcon sx={{ mr: 1, color: 'info.main' }} />
        <Typography variant="h6">{t('quickSaleSettingsModal.itemNotes.title')}</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          size="small"
          label={t('quickSaleSettingsModal.itemNotes.newNoteLabel')}
          placeholder={t('quickSaleSettingsModal.itemNotes.newNotePlaceholder')}
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          error={isDuplicate}
          helperText={
            isDuplicate
              ? t('quickSaleSettingsModal.itemNotes.duplicate')
              : isFull
                ? t('quickSaleSettingsModal.itemNotes.limitReached', { count: MAX_NOTE_COUNT })
                : ' '
          }
          inputProps={{ maxLength: MAX_NOTE_LENGTH }}
          sx={{ flex: 1, maxWidth: 420 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!trimmed || isDuplicate || isFull}
          onClick={handleAdd}
          sx={{ height: 40 }}
        >
          {t('quickSaleSettingsModal.itemNotes.add')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AutoAwesomeIcon />}
          disabled={missingSuggestions.length === 0 || isFull}
          onClick={() => onChange([...notes, ...missingSuggestions].slice(0, MAX_NOTE_COUNT))}
        >
          {t('quickSaleSettingsModal.itemNotes.addSuggested', { count: missingSuggestions.length })}
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<DeleteSweepIcon />}
          disabled={notes.length === 0}
          onClick={() => onChange([])}
        >
          {t('quickSaleSettingsModal.itemNotes.clearAll')}
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('quickSaleSettingsModal.itemNotes.listTitle', { count: notes.length })}
      </Typography>

      {notes.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
          {t('quickSaleSettingsModal.itemNotes.empty')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {notes.map((note, index) => (
            <Box
              key={`${note}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 5,
                pl: 0.5,
                bgcolor: 'background.paper',
              }}
            >
              <Tooltip title={t('quickSaleSettingsModal.itemNotes.moveLeft')}>
                <Button
                  size="small"
                  disabled={index === 0}
                  onClick={() => handleMove(index, -1)}
                  sx={{ minWidth: 26, px: 0, color: 'text.secondary' }}
                >
                  <ArrowBackIcon sx={{ fontSize: 12 }} />
                </Button>
              </Tooltip>
              <Chip
                label={note}
                onDelete={() => handleDelete(index)}
                color="info"
                variant="outlined"
                sx={{ fontSize: '0.9rem', border: 'none' }}
              />
              <Tooltip title={t('quickSaleSettingsModal.itemNotes.moveRight')}>
                <Button
                  size="small"
                  disabled={index === notes.length - 1}
                  onClick={() => handleMove(index, 1)}
                  sx={{ minWidth: 26, px: 0, color: 'text.secondary' }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 12 }} />
                </Button>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default ItemNotesSettingsTab;
